import {DateTime} from "luxon";

/**
 * Narrow a possibly-invalid DateTime to a valid one.
 *
 * @types/luxon 3.7 deleted DateTimeMaybeValid, the DateTime<Valid> | DateTime<Invalid> union that
 * DateTime.fromISO and friends used to return. They now return a single DateTime<boolean> whose
 * isValid is a plain boolean rather than a union discriminant, so "if (!d.isValid)" no longer
 * narrows anything and every "return d" against a DateTime<true> signature fails to compile.
 * This is that narrowing, stated explicitly. It is a genuine runtime check, not a cast.
 */
export function isValidDateTime(date: DateTime<boolean>): date is DateTime<true> {
  return date.isValid;
}


function parseDate(val: unknown): DateTime<true> {
  const date = tryParseDate(val);
  if (!date) {
    throw new Error('Invalid date');
  }
  return date;
}

function tryParseDate(val: unknown): DateTime<true> | null {
  if (typeof val === 'string') {
    if (val.length === 10) {
      val += 'T00:00:00';
    }
    const date = DateTime.fromISO(val, {setZone: true});
    if (!isValidDateTime(date)) {
      return null;
    }
    return date;
  } else if (val instanceof DateTime) {
    return isValidDateTime(val) ? val : null;
  } else if (val instanceof Date) {
    if (isNaN(val.getTime())) {
      return null;
    }
    const date = DateTime.fromJSDate(val);
    if (!isValidDateTime(date)) {
      return null;
    }
    return date;
  } else if (typeof val === 'number') {
    // check for timestamp length
    if (val.toString().length !== 13) {
      return null;
    }
    const date = DateTime.fromMillis(val);
    if (!isValidDateTime(date)) {
      return null;
    }
    return date;
  }
  return null;
}

function tryParseTime(val: unknown): [number, number] | null {
  if (typeof val === 'string') {
    const [hours, minutes] = val.split(':').map(Number);
    if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return [hours, minutes];
    }
  }
  return null;
}

function fromDateAndTime(date: DateTime<true> | string, time: string | [number, number]): DateTime<true> {
  date = tryParseDate(date)!;
  if (!date) {
    throw new Error('Invalid date');
  }
  if (typeof time === 'string') {
    time = parseTime(time);
  }
  const [hours, minutes] = time;
  return date.set({ hour: hours, minute: minutes });
}

function parseTime(time: string): [number, number] {
  const parts = tryParseTime(time);
  if (!parts) {
    throw new Error('Invalid time');
  }
  return parts;
}

function splitDateAndTime(dateTime: DateTime<true>): { date: DateTime<true>, time: [number, number] } {
  return {
    date: removeTime(dateTime),
    time: extractTime(dateTime),
  }
}

function extractTime(dateTime: DateTime<true>): [number, number] {
  return [dateTime.hour, dateTime.minute];
}

function removeTime(date: DateTime<true>): DateTime<true> {
  return date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
}


function toDateOnlyString(date: DateTime<true>): string {
  return date.toISODate();
}

function toTimeString(date: DateTime<true> | [number, number]): string {
  const [hours, minutes] = Array.isArray(date) ? date : extractTime(date);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

export default {
  isValidDateTime,
  tryParseDate,
  tryParseTime,
  parseDate,
  parseTime,
  fromDateAndTime,
  removeTime,
  extractTime,
  splitDateAndTime,
  toDateAndTimeString: (date: DateTime<true>): { date: string, time: string } => {
    const { date: datePart, time: timePart } = splitDateAndTime(date);
    return { date: toDateOnlyString(datePart), time: toTimeString(timePart) };
  },
  toDateOnlyString,
  toTimeString,
}